/**
 * VERIFICACIÓN MULTI-OT DESDE EL LISTADO GLOBAL — FASE 4.3-F (L99, punto 3)
 *
 * Misma filosofía visual y de datos que `RevisionMultiOt`
 * (`confirmacion-multi-ot.tsx`): selección de varias OT en
 * `PaginaOtsGlobal` → modal operativo con sus líneas CONFIRMADA,
 * agrupadas por OT, fecha/observación comunes opcionales, resultado
 * parcial. La diferencia real es de datos, no de forma: verificar no
 * lleva ejecutores (`DatosAccionLinea`, sin `ejecutoresReales`) — el
 * `POST /api/ot-lineas/verificar-masivo` (FASE 4.2-C) ya acepta líneas
 * de CUALQUIER OT en el mismo lote (nunca tomó un `idOt` como parámetro,
 * sólo `idLinea` por elemento — auditado antes de escribir esto: no hace
 * falta ningún endpoint nuevo, sólo esta pantalla).
 *
 * Elegible = `accionesPermitidas.verificar` (línea CONFIRMADA). Una
 * selección de OT puede traer líneas en cualquier estado (PENDIENTE,
 * CONFIRMADA, VERIFICADA, NO_OK…) — sólo las CONFIRMADA entran en el
 * lote; el resto ni se lista aquí, exactamente igual que
 * `RevisionMultiOt` sólo lista las confirmables. El servidor revalida
 * cada línea igualmente: esto es una propuesta de lote, nunca la
 * autoridad.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { api, type ItemVerificarMasivo, type OtDetalle, type RechazoLinea, type ResultadoAccionMasiva } from '../api.ts';
import {
  ahoraParaInput,
  Cargando,
  Cifra,
  Error as ErrorCaja,
  EstadoLinea,
  Modal,
  ResumenSeleccionMultiOt,
  useDatos,
  Vacio,
} from './base.tsx';

interface FilaVerificarMultiOt {
  readonly idOt: string;
  readonly otCodigo: string;
  readonly idLinea: string;
  readonly ubicacionCodigo: string;
  readonly ubicacionRuta: string;
  readonly descripcionTarea: string | null;
  readonly tipoDescripcion: string;
  readonly estadoLinea: string;
  readonly confirmadaPor: string | null;
  readonly idOperacionCliente: string;
  fechaEfectiva: string;
  observacion: string;
}

async function cargarOts(idsOt: readonly string[]): Promise<readonly OtDetalle[]> {
  return Promise.all(idsOt.map((id) => api.get<OtDetalle>(`/api/ots/${id}`)));
}

export function VerificacionMultiOt({
  idsOt,
  cerrar,
  alTerminar,
}: {
  idsOt: readonly string[];
  cerrar: () => void;
  alTerminar: () => void;
}): ReactNode {
  const carga = useDatos(() => cargarOts(idsOt), [idsOt.join(',')]);
  const [filas, setFilas] = useState<FilaVerificarMultiOt[] | null>(null);
  const [etiquetas, setEtiquetas] = useState<Readonly<Record<string, { otCodigo: string; ubicacionCodigo: string }>>>(
    {},
  );
  const [fechaComun, setFechaComun] = useState('');
  const [observacionComun, setObservacionComun] = useState('');
  const [resultado, setResultado] = useState<ResultadoAccionMasiva | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (carga.datos === null || filas !== null) return;
    const iniciales: FilaVerificarMultiOt[] = [];
    const nuevasEtiquetas: Record<string, { otCodigo: string; ubicacionCodigo: string }> = {};
    for (const ot of carga.datos) {
      for (const l of ot.lineas) {
        if (!l.accionesPermitidas.verificar) continue;
        iniciales.push({
          idOt: ot.id,
          otCodigo: ot.codigo,
          idLinea: l.id,
          ubicacionCodigo: l.ubicacionCodigo,
          ubicacionRuta: l.ubicacionRuta,
          descripcionTarea: l.descripcionTarea,
          tipoDescripcion: l.tipoDescripcion,
          estadoLinea: l.estado,
          confirmadaPor: l.confirmacion?.actor.nombre ?? null,
          idOperacionCliente: crypto.randomUUID(),
          fechaEfectiva: ahoraParaInput(),
          observacion: '',
        });
        nuevasEtiquetas[l.id] = { otCodigo: ot.codigo, ubicacionCodigo: l.ubicacionCodigo };
      }
    }
    setFilas(iniciales);
    setEtiquetas(nuevasEtiquetas);
  }, [carga.datos, filas]);

  function actualizarFila(idLinea: string, cambios: Partial<FilaVerificarMultiOt>): void {
    setFilas((prev) => (prev === null ? null : prev.map((f) => (f.idLinea === idLinea ? { ...f, ...cambios } : f))));
  }

  function aplicarComunes(): void {
    setFilas((prev) =>
      prev === null
        ? null
        : prev.map((f) => ({
            ...f,
            fechaEfectiva: fechaComun !== '' ? fechaComun : f.fechaEfectiva,
            observacion: observacionComun.trim() !== '' ? observacionComun : f.observacion,
          })),
    );
  }

  //  UX-POLISH-01 punto 5: igual que en RevisionMultiOt — nunca dejar que
  //  una línea no elegible (ya verificada, pendiente, NO OK…) desaparezca
  //  sin explicación.
  const lineasNoElegibles = (carga.datos ?? []).flatMap((ot) => ot.lineas).filter((l) => !l.accionesPermitidas.verificar);

  async function enviar(): Promise<void> {
    if (filas === null || filas.length === 0) return;
    setEnviando(true);
    setError(null);
    try {
      const lineas: ItemVerificarMasivo[] = filas.map((f) => ({
        idLinea: f.idLinea,
        fechaEfectiva: new Date(f.fechaEfectiva).toISOString(),
        observacion: f.observacion.trim() === '' ? null : f.observacion.trim(),
        idOperacionCliente: f.idOperacionCliente,
      }));
      const r = await api.post<ResultadoAccionMasiva>('/api/ot-lineas/verificar-masivo', { lineas });
      setResultado(r);
      const idsOk = new Set(r.procesadas.map((p) => p.idLinea));
      setFilas((prev) => (prev ?? []).filter((f) => !idsOk.has(f.idLinea)));
      alTerminar();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      titulo={`Verificar líneas de ${String(idsOt.length)} OT seleccionada(s)`}
      cerrar={cerrar}
      pie={
        filas !== null &&
        filas.length > 0 && (
          <>
            <span className="modal-pie-relleno" />
            {error !== null && <ErrorCaja error={error} />}
            <button type="button" className="principal" disabled={enviando} onClick={() => void enviar()}>
              {enviando ? 'Enviando…' : `Verificar ${String(filas.length)} línea(s)`}
            </button>
          </>
        )
      }
    >
      {carga.cargando && carga.datos === null && <Cargando texto="Cargando líneas confirmadas de las OT seleccionadas…" />}
      {carga.error !== null && <ErrorCaja error={carga.error} reintentar={carga.recargar} />}

      {filas !== null &&
        (filas.length === 0 ? (
          resultado === null && (
            <Vacio titulo="No hay líneas confirmadas pendientes de verificar en las OT seleccionadas" />
          )
        ) : (
          <>
            <ResumenSeleccionMultiOt
              otsSeleccionadas={idsOt.length}
              otsAfectadas={new Set(filas.map((f) => f.idOt)).size}
              lineasElegibles={filas.length}
              lineasNoElegibles={lineasNoElegibles}
              etiquetaAccion="verificables"
            />
            <p className="pequena suave">
              El servidor revalida cada línea de todos modos, con independencia de qué se muestre aquí.
            </p>

            <div className="modal-comunes">
              <div className="campo" style={{ margin: 0 }}>
                <label htmlFor="verificar-fecha-comun">Fecha efectiva común (opcional)</label>
                <input
                  id="verificar-fecha-comun"
                  type="datetime-local"
                  value={fechaComun}
                  max={ahoraParaInput()}
                  onChange={(e) => setFechaComun(e.target.value)}
                />
              </div>
              <div className="campo" style={{ margin: 0, flex: 1, minWidth: 200 }}>
                <label htmlFor="verificar-observacion-comun">Observación común (opcional)</label>
                <input
                  id="verificar-observacion-comun"
                  type="text"
                  value={observacionComun}
                  onChange={(e) => setObservacionComun(e.target.value)}
                  placeholder="Se copia a todas las líneas al aplicar"
                />
              </div>
              <button
                type="button"
                className="pequeno"
                disabled={fechaComun === '' && observacionComun.trim() === ''}
                onClick={aplicarComunes}
              >
                Aplicar a todas las líneas
              </button>
            </div>

            {carga.datos?.map((ot) => {
              const filasDeEstaOt = filas.filter((f) => f.idOt === ot.id);
              if (filasDeEstaOt.length === 0) return null;
              return (
                <div key={ot.id} className="modal-grupo-ot">
                  <div className="modal-grupo-ot-cabecera">
                    <Link to={`/ots/${ot.id}`} className="mono">
                      {ot.codigo}
                    </Link>
                    <span className="suave pequena">{ot.centroDescripcion}</span>
                    <span className="pequena tenue">
                      {filasDeEstaOt.length} línea{filasDeEstaOt.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="lote-bulk-filas">
                    {filasDeEstaOt.map((f) => (
                      <FilaVerificarEditable key={f.idLinea} fila={f} onCambiar={(c) => actualizarFila(f.idLinea, c)} />
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        ))}

      {resultado !== null && (
        <ResultadoVerificarMultiOtPanel resultado={resultado} etiquetas={etiquetas} cerrar={() => setResultado(null)} />
      )}
    </Modal>
  );
}

function FilaVerificarEditable({
  fila,
  onCambiar,
}: {
  fila: FilaVerificarMultiOt;
  onCambiar: (cambios: Partial<FilaVerificarMultiOt>) => void;
}): ReactNode {
  return (
    <div className="lote-bulk-fila">
      <div className="lote-bulk-fila-cabecera" style={{ flexWrap: 'wrap' }}>
        <span className="cod">{fila.ubicacionCodigo}</span>
        <span className="pequena suave">{fila.descripcionTarea ?? fila.tipoDescripcion}</span>
        <EstadoLinea estado={fila.estadoLinea} />
      </div>
      {fila.confirmadaPor !== null && (
        <div className="pequena tenue" style={{ marginBottom: 8 }}>
          Confirmada por {fila.confirmadaPor}
        </div>
      )}
      <div className="fila" style={{ gap: 10, flexWrap: 'wrap' }}>
        <input
          type="datetime-local"
          aria-label={`Fecha efectiva — ${fila.otCodigo} ${fila.ubicacionCodigo}`}
          value={fila.fechaEfectiva}
          max={ahoraParaInput()}
          onChange={(e) => onCambiar({ fechaEfectiva: e.target.value })}
        />
        <input
          type="text"
          aria-label={`Observación — ${fila.otCodigo} ${fila.ubicacionCodigo}`}
          placeholder="Observación (opcional)"
          value={fila.observacion}
          onChange={(e) => onCambiar({ observacion: e.target.value })}
          style={{ flex: 1, minWidth: 160 }}
        />
      </div>
    </div>
  );
}

function ResultadoVerificarMultiOtPanel({
  resultado,
  etiquetas,
  cerrar,
}: {
  resultado: ResultadoAccionMasiva;
  etiquetas: Readonly<Record<string, { otCodigo: string; ubicacionCodigo: string }>>;
  cerrar: () => void;
}): ReactNode {
  const nuevas = resultado.procesadas.filter((p) => !p.yaEstaba).length;
  const yaEstaban = resultado.procesadas.filter((p) => p.yaEstaba).length;

  function etiquetaDe(r: RechazoLinea): { otCodigo: string; ubicacionCodigo: string } {
    return etiquetas[r.idLinea] ?? { otCodigo: '—', ubicacionCodigo: r.idLinea };
  }

  return (
    <div className="panel resultado-bulk" style={{ marginTop: 16 }}>
      <div className="panel-cabecera">
        <h3>Resultado — verificar en lote</h3>
        <button type="button" className="pequeno" onClick={cerrar}>
          Cerrar
        </button>
      </div>
      <div className="panel-cuerpo">
        <div className="cifras">
          <Cifra n={nuevas} t="procesadas" />
          <Cifra n={yaEstaban} t="ya estaban realizadas" />
          <Cifra n={resultado.rechazadas.length} t="rechazadas" />
        </div>
        {resultado.rechazadas.length > 0 && (
          <div className="sep">
            <p className="pequena suave">Líneas rechazadas — el servidor revalidó cada una por separado:</p>
            <ul className="lista-rechazadas">
              {resultado.rechazadas.map((r) => {
                const et = etiquetaDe(r);
                return (
                  <li key={r.idLinea}>
                    <div className="fila" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span className="mono pequena">{et.otCodigo}</span>
                      <span className="cod pequena">{et.ubicacionCodigo}</span>
                      <span className="etiqueta error pequena">{r.codigo}</span>
                    </div>
                    <div className="pequena suave">{r.mensaje}</div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
